import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { Camera, FileText, Sparkles, Loader2, CheckCircle2, ArrowLeft, Shield, Activity, Upload, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';
import getServices from '../../services/serviceRouter';

const LENS_DEMOS = {
  ccda: {
    label: 'CCDA / CCD',
    desc: 'Continuity of Care Document',
    sections: [
      { title: 'Demographics', items: [
        { key: 'Name', val: 'John Smith' }, { key: 'DOB', val: '03/15/1965' },
        { key: 'MRN', val: 'MRN-001234' }, { key: 'Insurance', val: 'Blue Cross Blue Shield' },
      ]},
      { title: 'Active Problems', items: [
        { key: 'E11.9', val: 'Type 2 diabetes mellitus — Active since 2018' },
        { key: 'I10', val: 'Essential hypertension — Active since 2015' },
        { key: 'E78.5', val: 'Hyperlipidemia — Active since 2019' },
      ]},
      { title: 'Medications', items: [
        { key: 'Metformin 500mg', val: 'BID — Refills: 3' },
        { key: 'Lisinopril 10mg', val: 'Daily — Refills: 5' },
        { key: 'Atorvastatin 20mg', val: 'QHS — Refills: 2' },
      ]},
      { title: 'Allergies', items: [
        { key: 'Penicillin', val: 'Rash — Moderate' },
        { key: 'Sulfa drugs', val: 'Hives — Mild' },
      ]},
    ],
  },
  lab: {
    label: 'Lab Report', desc: 'Blood work and pathology',
    sections: [
      { title: 'Report Info', items: [{ key: 'Collected', val: '02/01/2026 08:15 AM' }, { key: 'Reported', val: '02/02/2026 02:30 PM' }]},
      { title: 'Complete Metabolic Panel', items: [
        { key: 'Glucose', val: '142 mg/dL (H) — Ref: 70-100' }, { key: 'BUN', val: '18 mg/dL — Ref: 7-20' },
        { key: 'Creatinine', val: '1.1 mg/dL — Ref: 0.7-1.3' }, { key: 'eGFR', val: '72 mL/min (L) — Ref: >90' },
      ]},
      { title: 'Hemoglobin A1c', items: [{ key: 'HbA1c', val: '7.2% (H) — Ref: <5.7% | Target: <7.0%' }, { key: 'Est. Avg Glucose', val: '160 mg/dL' }]},
      { title: 'Lipid Panel', items: [
        { key: 'Total Cholesterol', val: '210 mg/dL (H) — Ref: <200' }, { key: 'LDL', val: '128 mg/dL (H) — Ref: <100' },
        { key: 'HDL', val: '48 mg/dL — Ref: >40' }, { key: 'Triglycerides', val: '170 mg/dL (H) — Ref: <150' },
      ]},
    ],
  },
  radiology: {
    label: 'Radiology', desc: 'Imaging reports',
    sections: [
      { title: 'Study', items: [{ key: 'Procedure', val: 'CT Abdomen/Pelvis with Contrast' }, { key: 'Date', val: '01/28/2026' }, { key: 'Radiologist', val: 'Dr. James Wong, MD' }]},
      { title: 'Findings', items: [
        { key: 'Liver', val: 'Mild hepatic steatosis. No focal lesions.' },
        { key: 'Kidneys', val: '1.2 cm simple cyst left lower pole. No hydronephrosis.' },
        { key: 'Aorta', val: 'Normal caliber. No aneurysm.' },
      ]},
      { title: 'Impression', items: [{ key: '1', val: 'Mild hepatic steatosis (fatty liver)' }, { key: '2', val: 'Simple left renal cyst — benign' }, { key: '3', val: 'Otherwise unremarkable' }]},
    ],
  },
  referral: {
    label: 'Referral Letter', desc: 'Incoming referral',
    sections: [
      { title: 'Referral Info', items: [{ key: 'From', val: 'Dr. Michael Park, MD — Cardiology' }, { key: 'Date', val: '01/15/2026' }]},
      { title: 'Reason', items: [{ key: 'Primary', val: 'Co-management of Type 2 DM with cardiac clearance' }]},
      { title: 'Cardiac Summary', items: [{ key: 'Echo', val: 'EF 60%, Grade 1 diastolic dysfunction' }, { key: 'Stress', val: 'Negative for ischemia at 10 METs' }]},
      { title: 'Recommendations', items: [{ key: '1', val: 'Continue cardiac medications' }, { key: '2', val: 'Optimize glycemic control — HbA1c 7.2%' }, { key: '3', val: 'Cardiology follow-up in 12 months' }]},
    ],
  },
  discharge: {
    label: 'Discharge Summary', desc: 'Hospital discharge',
    sections: [
      { title: 'Admission', items: [{ key: 'Admitted', val: '12/20/2025' }, { key: 'Discharged', val: '12/23/2025' }, { key: 'Reason', val: 'Diabetic ketoacidosis (DKA)' }]},
      { title: 'Hospital Course', items: [
        { key: 'Day 1', val: 'Glucose 420. Insulin drip, IV fluids.' },
        { key: 'Day 2', val: 'Anion gap closed. Transitioned to subQ insulin.' },
        { key: 'Day 3', val: 'Glucose stable 140-180. Discharged.' },
      ]},
      { title: 'Discharge Meds', items: [{ key: 'Metformin 500mg', val: 'BID (resumed)' }, { key: 'Insulin Glargine 20u', val: 'QHS (NEW)' }, { key: 'Lisinopril 10mg', val: 'Daily (continued)' }]},
      { title: 'Follow-Up', items: [{ key: '1', val: 'PCP within 7 days' }, { key: '2', val: 'Endocrinology referral for insulin titration' }, { key: '3', val: 'Recheck BMP and HbA1c in 2 weeks' }]},
    ],
  },
  // ── Specialty-specific MA intake capture ──
  peds_vitals: {
    label: 'Peds Vitals Card', desc: 'MA-scanned growth intake',
    sections: [
      { title: 'Growth', items: [
        { key: 'Weight', val: '12.4 kg' }, { key: 'Length', val: '87.2 cm' },
        { key: 'Head Circumference', val: '48.2 cm' }, { key: 'BMI-for-age', val: '16.3' },
      ]},
      { title: 'Vitals', items: [
        { key: 'HR', val: '108 bpm' }, { key: 'Temp', val: '98.6°F' },
        { key: 'SpO2', val: '99%' }, { key: 'Resp', val: '24/min' },
      ]},
      { title: 'Developmental Screen', items: [
        { key: 'Tool', val: 'ASQ-3 (24 mo)' }, { key: 'Communication', val: 'Pass' },
        { key: 'Gross Motor', val: 'Pass' }, { key: 'Problem Solving', val: 'Monitor' },
      ]},
      { title: 'Vaccines (parent-reported)', items: [
        { key: 'HepB', val: '3/3 complete' }, { key: 'DTaP', val: '4/5 — booster due' },
        { key: 'MMR', val: '1/2 — #2 due' }, { key: 'Varicella', val: '1/2 — #2 due' },
      ]},
    ],
  },
  psych_screener: {
    label: 'Psych Screener Forms', desc: 'PHQ-9 / GAD-7 / C-SSRS',
    sections: [
      { title: 'PHQ-9 (Depression)', items: [
        { key: 'Total score', val: '14 / 27 (NEW)' }, { key: 'Severity', val: 'Moderate depression' },
        { key: 'Item 9 (SI)', val: '1 — Several days' }, { key: 'Date administered', val: 'Today' },
      ]},
      { title: 'GAD-7 (Anxiety)', items: [
        { key: 'Total score', val: '9 / 21' }, { key: 'Severity', val: 'Mild anxiety' },
        { key: 'Trend (vs last visit)', val: 'Improved: 12 → 9' },
      ]},
      { title: 'C-SSRS (Suicide Risk)', items: [
        { key: 'Ideation (lifetime)', val: 'Yes — passive' }, { key: 'Ideation (past month)', val: 'No' },
        { key: 'Plan', val: 'No' }, { key: 'Intent', val: 'No' }, { key: 'Behavior', val: 'No' },
      ]},
      { title: 'Adherence + Safety', items: [
        { key: 'Medication adherence', val: 'Good (parent-confirmed)' },
        { key: 'Safety plan on file', val: 'Yes — reviewed today' },
        { key: 'Prior hospitalizations', val: 'None' },
      ]},
    ],
  },
  obgyn_labs: {
    label: 'Prenatal Labs', desc: 'MA-scanned lab slip + intake',
    sections: [
      { title: 'Dating', items: [
        { key: 'LMP', val: '10/15/2025' }, { key: 'EDD (Naegele)', val: '07/22/2026' },
        { key: 'GA today', val: '28 w 0 d' }, { key: 'Trimester', val: '3rd' },
      ]},
      { title: 'Prenatal Labs', items: [
        { key: 'CBC Hgb', val: '11.4 g/dL' }, { key: 'Type & Screen', val: 'O+, antibody neg' },
        { key: 'HIV / Syphilis / HepB', val: 'Non-reactive' }, { key: '1-hr GCT', val: '142 mg/dL (NEW)' },
      ]},
      { title: 'Obstetric Findings', items: [
        { key: 'Fundal height', val: '29 cm' }, { key: 'FHT', val: '148 bpm' },
        { key: 'Fetal movement', val: 'Present' }, { key: 'Cervix', val: 'Closed, long' },
      ]},
      { title: 'Risk + Vaccines', items: [
        { key: 'GBS status', val: 'Pending (draw at 36 wk)' }, { key: 'Tdap (this pregnancy)', val: 'Due — 27–36 wk window' },
        { key: 'Influenza', val: 'Administered 10/2025' },
      ]},
    ],
  },
  surg_preop: {
    label: 'Pre-op H&P', desc: 'ASA + anesthesia form',
    sections: [
      { title: 'Planned Procedure', items: [
        { key: 'Procedure', val: 'Right Total Knee Arthroplasty' },
        { key: 'CPT', val: '27447' }, { key: 'Surgery date', val: '03/08/2026' },
        { key: 'Surgeon', val: 'Dr. Kumar, MD' },
      ]},
      { title: 'Risk Stratification', items: [
        { key: 'ASA class', val: 'II' }, { key: 'METs', val: '6 — Adequate' },
        { key: 'Anesthesia plan', val: 'Spinal + sedation' },
        { key: 'Airway (Mallampati)', val: 'Class II' },
      ]},
      { title: 'Pre-op Labs', items: [
        { key: 'CBC', val: 'WNL' }, { key: 'BMP', val: 'WNL' },
        { key: 'INR', val: '1.0' }, { key: 'EKG', val: 'NSR 68, no ischemia' },
      ]},
      { title: 'Anticoag + Consent', items: [
        { key: 'Apixaban', val: 'Hold 48 h pre-op (NEW)' },
        { key: 'Surgical consent', val: 'Signed today' },
        { key: 'Anesthesia consent', val: 'Signed today' },
      ]},
    ],
  },
};

const SPECIALTY_KEYS = new Set(['peds_vitals', 'psych_screener', 'obgyn_labs', 'surg_preop']);

const LensDemoDialog = ({ open, onOpenChange, patientName, patientId, defaultDemoKey }) => {
  const { isDemo } = useAppMode();
  const { mode } = useAppMode();
  const lens = getServices(mode).lens;
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [extractionSource, setExtractionSource] = useState(null);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [error, setError] = useState(null);

  const handleDemoSelect = (key) => {
    setSelectedDemo(key);
    setIsProcessing(true);
    setError(null);
    setTimeout(() => {
      setExtractedData({ label: LENS_DEMOS[key].label, sections: LENS_DEMOS[key].sections });
      setExtractionSource('demo');
      setIsProcessing(false);
      toast.success(`${LENS_DEMOS[key].label} processed`, { description: 'Structured data extracted by Lumina AI' });
    }, 1200);
  };

  // Auto-run a specialty demo when the dialog is opened with a defaultDemoKey.
  useEffect(() => {
    if (!open) return;
    if (!defaultDemoKey) return;
    if (selectedDemo || extractedData || isProcessing) return;
    handleDemoSelect(defaultDemoKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultDemoKey]);

  const handleRealUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedDemo('upload');
    setIsProcessing(true);
    setError(null);
    setExtractedData(null);

    try {
      const job = await lens.extractAndWait(file, {
        patientId: patientId || '',
        onProgress: (j) => setCurrentJobId(j.job_id),
      });
      setCurrentJobId(job.job_id);
      if (job.status === 'completed' && job.extracted_fields) {
        setExtractedData({ label: job.extracted_fields.document_type || 'Document', sections: job.extracted_fields.sections || [] });
        setExtractionSource(job.source || 'local');
        toast.success('Document processed', { description: job.extracted_fields.summary || 'Structured data extracted' });
      } else {
        setError(job.errors?.[0] || 'Extraction failed');
        toast.error('Extraction failed', { description: job.errors?.[0] });
      }
    } catch (err) {
      setError(err.message);
      toast.error('Upload failed', { description: err.message });
    }
    setIsProcessing(false);
  };

  const handleRetry = async () => {
    if (!currentJobId) return;
    setIsProcessing(true);
    setError(null);
    try {
      const retried = await lens.retryJob(currentJobId);
      if (retried.status === 'processing') {
        let job = retried;
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 2000));
          job = await lens.pollJob(job.job_id);
          if (job.status === 'completed' || job.status === 'failed') break;
        }
        if (job.status === 'completed' && job.extracted_fields) {
          setExtractedData({ label: job.extracted_fields.document_type || 'Document', sections: job.extracted_fields.sections || [] });
          setExtractionSource(job.source || 'local');
          setError(null);
          toast.success('Retry succeeded');
        } else {
          setError(job.errors?.[0] || 'Retry failed');
        }
      } else if (retried.status === 'completed' && retried.extracted_fields) {
        setExtractedData({ label: retried.extracted_fields.document_type || 'Document', sections: retried.extracted_fields.sections || [] });
        setExtractionSource(retried.source || 'local');
        setError(null);
      } else {
        setError(retried.errors?.[0] || 'Retry failed');
      }
    } catch (err) {
      setError(err.message);
    }
    setIsProcessing(false);
  };

  const handleClose = () => {
    setSelectedDemo(null);
    setExtractedData(null);
    setExtractionSource(null);
    setError(null);
    setCurrentJobId(null);
    setIsProcessing(false);
    onOpenChange(false);
  };

  const handleReset = () => {
    setSelectedDemo(null);
    setExtractedData(null);
    setExtractionSource(null);
    setError(null);
    setCurrentJobId(null);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="lens-demo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5 text-violet-600" />
            Lumina Lens {patientName ? `— ${patientName}` : ''}
          </DialogTitle>
          <DialogDescription>
            {error ? 'Extraction failed — retry or try a different document' :
             !extractedData ? 'Upload a real document or select a demo type' :
             `Extracted data from ${extractedData.label}`}
          </DialogDescription>
        </DialogHeader>

        {!extractedData && !isProcessing && !error && (
          <div className="space-y-4">
            {/* Real Upload */}
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">Upload Real Document</p>
              <label htmlFor="lens-dialog-upload" className="block border-2 border-dashed border-slate-200 hover:border-violet-400 rounded-xl p-4 text-center cursor-pointer hover:bg-violet-50/30 transition-all">
                <Upload className="h-6 w-6 text-violet-500 mx-auto mb-1" />
                <p className="text-xs font-semibold text-slate-700">Click to upload (PNG, JPG, PDF)</p>
                <input id="lens-dialog-upload" type="file" className="hidden" accept="image/*,.pdf" onChange={handleRealUpload} data-testid="lens-dialog-file-input" />
              </label>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-[10px] text-slate-400 uppercase">or try a demo</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>

            {/* Demo Selector */}
            <div>
              <div className="grid grid-cols-5 gap-2" data-testid="lens-chart-demo-grid">
                {Object.entries(LENS_DEMOS).filter(([key]) => !SPECIALTY_KEYS.has(key)).map(([key, demo]) => (
                  <button
                    key={key}
                    onClick={() => handleDemoSelect(key)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-all text-center"
                    data-testid={`lens-chart-demo-${key}`}
                  >
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-slate-600" />
                    </div>
                    <p className="text-xs font-semibold text-slate-800">{demo.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-snug">{demo.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-2.5 flex items-start gap-2">
                <Shield className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-amber-700 leading-relaxed">Do not upload any personal information such as NPI, PHI, or other protected data.</p>
              </div>
              <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-2.5 flex items-start gap-2">
                <Activity className="h-3.5 w-3.5 text-blue-600 mt-0.5 shrink-0" />
                <p className="text-[10px] text-blue-700 leading-relaxed">This is a trial site. Features available on the live site once you sign up will be more comprehensive and robust.</p>
              </div>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="py-12 text-center">
            <Loader2 className="h-10 w-10 text-slate-600 mx-auto animate-spin mb-3" />
            <p className="font-medium text-slate-900">Processing with Lumina AI...</p>
            <p className="text-sm text-slate-500 mt-1">Extracting structured data</p>
          </div>
        )}

        {error && !isProcessing && (
          <div className="py-8 text-center space-y-3">
            <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
            <p className="font-medium text-slate-900">Extraction Failed</p>
            <p className="text-sm text-red-600">{error}</p>
            <div className="flex gap-2 justify-center">
              <Button size="sm" variant="outline" onClick={handleReset} data-testid="lens-dialog-try-another">Try Another</Button>
              {currentJobId && (
                <Button size="sm" onClick={handleRetry} className="gap-1 bg-black hover:bg-gray-800" data-testid="lens-dialog-retry-btn">
                  <RefreshCw className="h-3 w-3" /> Retry
                </Button>
              )}
            </div>
          </div>
        )}

        {extractedData && !isProcessing && !error && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-50 text-emerald-700 text-xs gap-1"><Sparkles className="h-3 w-3" /> AI Extracted</Badge>
                {extractionSource && <Badge variant="outline" className="text-[10px]">{extractionSource}</Badge>}
              </div>
              <Button size="sm" variant="outline" className="gap-1 text-xs" onClick={handleReset} data-testid="lens-chart-try-another">
                <ArrowLeft className="h-3 w-3" /> Try Another
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-3" data-testid="lens-chart-extracted">
              {extractedData.sections.map((section, si) => (
                <Card key={si} className="border-slate-200">
                  <CardHeader className="py-2 px-3">
                    <CardTitle className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="px-3 pb-3">
                    <div className="space-y-1">
                      {section.items.map((item, ii) => (
                        <div key={ii} className="flex items-start gap-2 text-sm border-b last:border-0 border-slate-100 pb-1 last:pb-0">
                          <span className="text-xs font-medium text-slate-500 shrink-0 w-28">{item.key}</span>
                          <span className={cn("text-xs", item.val.includes('(H)') ? 'text-red-600 font-medium' : item.val.includes('(L)') ? 'text-amber-600 font-medium' : item.val.includes('(NEW)') ? 'text-blue-600 font-medium' : 'text-slate-700')}>
                            {item.val}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Close</Button>
          {extractedData && !error && (
            <Button className="bg-black hover:bg-gray-800 gap-2" onClick={() => { toast.success('Data added to patient chart'); handleClose(); }} data-testid="lens-chart-add-to-chart">
              <CheckCircle2 className="h-4 w-4" /> Add to Chart
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LensDemoDialog;
